import { useMemo, useState } from 'react';
import type { Member } from '../../api/groups';
import { useFuzzySearch } from '../../hooks/useFuzzySearch';
import { useShareModalContext } from './useShareModalContext';
import { createMemberForEmptyGroup } from './util/MemberSearch';

export type MemberWithGroup = Member & { groupId: string; groupName: string; searchField: string; searchValue: string };

const useMemberSearch = () => {
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<MemberWithGroup[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const { groupsBySchool, membersByGroup } = useShareModalContext();

  const allMembers: MemberWithGroup[] = useMemo(
    () =>
      Object.values(groupsBySchool)
        .flat()
        .flatMap(group => {
          const isEmpty = membersByGroup[group.id].length === 0;
          return (isEmpty ? [createMemberForEmptyGroup(group.id, group.name)] : membersByGroup[group.id]).map(m => ({
            ...m,
            groupId: group.id,
            groupName: group.name,
            searchField: `${m.lastName} ${m.firstName} ${group.name}`,
            searchValue,
          }));
        }),
    [groupsBySchool, membersByGroup, searchValue],
  );

  const fuzzySearch = useFuzzySearch(allMembers, 'searchField');

  const handleSearch = () => {
    if (!searchValue.trim()) return handleClearSearch();

    setIsSearching(true);
    setSearchResults(fuzzySearch(searchValue));
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSearchResults([]);
    setIsSearching(false);
  };

  return {
    searchValue,
    setSearchValue,
    handleSearch,
    searchResults,
    handleClearSearch,
    isSearching,
  };
};

export default useMemberSearch;
